import React, { useMemo } from 'react';
import SEO from '../components/SEO';
import PageBanner from '../components/PageBanner';
import Reveal from '../components/ui/Reveal';
import usePage from '../hooks/usePage';
import { UPLOADS_URL } from '../api/axios';

const DEFAULT_VALUES = [
  {
    title: 'Empathy',
    subtitle: 'Understanding Beyond Ourselves',
    icon: 'fa-heart',
    image: '/images/core-values/Empthy_sa_1280x720.jpg',
    desc: 'Academic intelligence becomes more meaningful when it is supported by emotional intelligence. We help children understand feelings, support one another and develop the compassion needed to build strong and meaningful relationships.',
  },
  {
    title: 'Confidence',
    subtitle: 'Finding Their Own Voice',
    icon: 'fa-star',
    image: '/images/core-values/confidence.webp',
    desc: 'We create opportunities for children to ask questions, express ideas, participate, perform, lead and make decisions. Our aim is to help every student develop the confidence to believe in their abilities and face challenges positively.',
  },
  {
    title: 'Curiosity',
    subtitle: 'Inspiring a Love for Learning',
    icon: 'fa-lightbulb',
    image: '/images/core-values/curiosity.jpg.webp',
    desc: 'We encourage students to look beyond textbooks, ask "why?", explore possibilities and discover answers independently. Curiosity turns education into a lifelong journey rather than simply preparation for examinations.',
  },
  {
    title: 'Responsibility',
    subtitle: 'Preparing for Life',
    icon: 'fa-circle-check',
    image: '/images/core-values/responsiblity_saws.webp',
    desc: 'Students learn that their actions have an impact on themselves, their school, their community and the world around them. We nurture responsible decision-making, independence and a strong sense of social awareness.',
  },
  {
    title: 'Collaboration',
    subtitle: 'Growing Together',
    icon: 'fa-people-arrows',
    image: '/images/core-values/collobartion.jpg.webp',
    desc: 'Success in the modern world requires the ability to listen, communicate and work with others. Through teamwork and shared experiences, students learn that different strengths and perspectives can create better outcomes.',
  },
  {
    title: 'Leadership',
    subtitle: 'Creating Positive Impact',
    icon: 'fa-crown',
    image: '/images/core-values/Leadership.jpg.webp',
    desc: 'At St. Andrews World School, leadership is not simply about being in charge. It is about taking initiative, making responsible decisions and inspiring others through positive action.',
  },
];

const CoreValuesPage = () => {
  const pageData = usePage('core-values');
  const sections = pageData.sections || {};

  const banner = sections.banner || pageData.banner || {};
  const bannerTitle = banner.title || pageData.title || 'Core Values';
  const bannerSubtitle = banner.subtitle || 'Guiding Principles Shaping Character, Compassion, and Leadership';
  const bannerBreadcrumb = banner.breadcrumb || 'CORE VALUES';
  const bannerImgSrc = pageData.bannerImage || '/images/CORE-VALUES-1500x430.jpg';

  const intro =
    sections.intro ||
    'At St. Andrews World School, our core values shape the way children learn, interact, grow and prepare for life. We believe education is not only about academic achievement — it is about building confident minds, compassionate hearts and responsible individuals.';

  const rawValues = sections.values && sections.values.length > 0 ? sections.values : DEFAULT_VALUES;
  const values = useMemo(() => {
    return rawValues.map((v, i) => {
      const def = DEFAULT_VALUES[i] || {};
      const img = v.image || def.image || '/images/CORE-VALUES-1500x430.jpg';
      const fullImg =
        img.startsWith('http') || img.startsWith('/')
          ? img
          : `${UPLOADS_URL}/${img}`;

      return {
        title: v.title || v.key || def.title || `Core Value #${i + 1}`,
        subtitle: v.subtitle || v.sub || def.subtitle || '',
        icon: v.icon || def.icon || 'fa-star',
        image: fullImg,
        desc: v.desc || v.text || def.desc || '',
      };
    });
  }, [rawValues]);

  const lifeValues = sections.life_values || {};
  const lifeHeading = lifeValues.heading || 'Values That Stay With Children for Life';
  const lifeParagraphsRaw =
    lifeValues.paragraphs ||
    "Parents searching for the best school look beyond academics. They want an environment where their child develops strong values, emotional confidence, independence and the ability to make thoughtful decisions.\n\nAt St. Andrews World School, our values are woven into everyday learning, teacher-student relationships and school experiences so that children grow not only into successful students, but into kind, capable and future-ready individuals.";

  const lifeParagraphs = useMemo(() => {
    return String(lifeParagraphsRaw)
      .replace(/\r/g, '')
      .split(/\n\s*\n|\n/)
      .map((p) => p.trim())
      .filter(Boolean);
  }, [lifeParagraphsRaw]);

  return (
    <>
      <SEO
        title={bannerTitle}
        description="Core Values of St. Andrews World School, Sonipat. Character, compassion, and leadership guiding every learner."
        keywords="Core Values, Empathy, Leadership, Curiosity, SAWS Sonipat, St. Andrews World School"
        slug="core-values"
      />

      {/* ─── HERO BANNER ─────────────────────────────────────────── */}
      <PageBanner
        title={bannerTitle}
        subtitle={bannerSubtitle}
        breadcrumb={bannerBreadcrumb}
        image={bannerImgSrc}
        fallback="/images/CORE-VALUES-1500x430.jpg"
      />

      {/* ─── SECTION 1: INTRO (75% width on desktop) ─────────────── */}
      <section className="bg-white py-12 md:py-16">
        <div className="w-full lg:w-[75%] lg:max-w-[75%] mx-auto px-5 lg:px-8 text-center">
          <Reveal>
            <p className="text-gray-600 text-lg md:text-ml leading-relaxed font-normal">
              {intro}
            </p>
          </Reveal>
        </div>
      </section>

      {/* ─── SECTION 2: 6 CORE VALUES GRID ───────────────────────── */}
      <section className="bg-white pb-16 md:pb-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((v, i) => (
              <Reveal key={v.title + i} delayMs={i * 50} className="h-full">
                <div className="group relative bg-white border border-gray-200 transition-all duration-300 hover:border-[#d8a85f] hover:shadow-xl hover:-translate-y-2 h-full flex flex-col overflow-hidden rounded-none">
                  {/* Photo with hover zoom */}
                  <div className="relative overflow-hidden shrink-0 border-b border-gray-100 bg-gray-100">
                    <img
                      src={v.image}
                      alt={v.title}
                      loading="lazy"
                      className="w-full h-[240px] object-cover transition-transform duration-700 group-hover:scale-105"
                      onError={(e) => {
                        e.target.src = '/images/CORE-VALUES-1500x430.jpg';
                      }}
                    />
                  </div>

                  {/* Content & Floating Icon */}
                  <div className="p-8 flex flex-col flex-1 relative">
                    {/* Floating circular icon badge */}
                    <div className="absolute -top-8 right-6 w-16 h-16 bg-[#1b3160] text-white flex items-center justify-center shadow-lg border-4 border-white rounded-full transition-transform duration-300 group-hover:scale-110">
                      <i className={`fas ${v.icon} text-xl text-[#d8a85f]`}></i>
                    </div>

                    <div className="min-w-0 pr-14">
                      <h3 className="text-[#1b3160] font-bold text-2xl mb-1 font-serif">
                        {v.title}
                      </h3>
                      {v.subtitle && (
                        <p className="text-[#d8a85f] text-[13px] md:text-[14px] uppercase tracking-wider font-bold">
                          {v.subtitle}
                        </p>
                      )}
                    </div>

                    <div className="mt-4 border-t border-gray-100 pt-4 flex-1">
                      <p className="text-gray-600 text-[15px] leading-relaxed">
                        {v.desc}
                      </p>
                    </div>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─── SECTION 3: VALUES FOR LIFE ──────────────────────────── */}
      <section className="bg-[#f9f8f4] py-14 md:py-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 text-center">
          <Reveal>
            <h2 className="text-3xl md:text-5xl font-bold text-[#1b3160] mb-5 font-serif">
              {lifeHeading}
            </h2>
            <div className="w-16 h-1 bg-[#d8a85f] mx-auto mb-8 rounded-full"></div>

            <div className="space-y-6 text-gray-600 text-lg md:text-xl leading-relaxed">
              {lifeParagraphs.map((para, idx) => (
                <p key={idx}>{para}</p>
              ))}
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
};

export default CoreValuesPage;
